import React from 'react';
import moment from 'moment-timezone';
import i18next from 'i18next';

import {Text} from '@famly/mf_data-display_text';
import {Box} from '@famly/mf_layout_box';
import {Stack} from '@famly/mf_layout_stack';
import {hasValue} from '@famly/stat_ts-utils_has-value';
import {MfLookalikeTag} from 'web-app/react/foundation/components/mf-lookalike/mf-lookalike-tag';
import type {DateRangeInclusive} from 'web-app/react/schedule-break/helpers/date-range';
import {Icon} from '@famly/mf_data-display_icon';
import {useCurrencyFormatter} from 'web-app/react/bookings-finance/contexts/currency-formatter-context';

import type * as Types from '../../../types';

type Props = {
    plan: Types.OverviewPlanItem;
    isLast: boolean;
};
const PlanItem = ({plan, isLast}: Props) => {
    const {formatAmount} = useCurrencyFormatter();
    return (
        <Box
            padding={4}
            backgroundColor={plan.isSelected ? 'n25' : 'n50'}
            borderLeft={plan.isSelected ? 'p300' : 'n200'}
            borderLeftWidth={4}
            borderBottom={isLast ? undefined : 'n200'}
            data-e2e-id="plan-item"
            data-highlighted={plan.isSelected}
            data-last={isLast}
        >
            <Stack justifyContent="space-between" alignItems="center">
                <Stack direction="column" gap={3} justifyContent="center">
                    <Stack direction="row" gap={2} alignItems="center" data-e2e-id="plan-date">
                        <Icon name="calendar_today" color={plan.isSelected ? 'n400' : 'n300'} />
                        <Text
                            color={plan.isSelected ? 'n400' : 'n300'}
                            variant="body"
                            emphasized
                            data-e2e-id="plan-date-text"
                        >
                            {dateRangeCopy(plan.dateRange)}
                        </Text>
                    </Stack>
                    <Text variant="body" color="n300" data-e2e-id="plan-name">
                        {plan.type === 'plan' ? (
                            plan.tuitionName
                        ) : (
                            <i>{i18next.t('react.profile.childProfile.plansAndInvoices.translations.json.onBreak')}</i>
                        )}
                    </Text>
                </Stack>
                <Stack direction="column" gap={3} alignItems="flex-end">
                    {hasValue(plan.tag) ? (
                        <Box data-e2e-id="plan-tag">
                            <MfLookalikeTag
                                backgroundColor={plan.tag.type === 'in-effect' ? 'g400' : 'n75'}
                                label={plan.tag.text}
                                textColor={plan.tag.type === 'in-effect' ? 'n0' : 'n400'}
                                data-e2e-id="plan-tag-label"
                            />
                        </Box>
                    ) : null}
                    <Text variant="body" color="n300" data-e2e-id="plan-amount">
                        {plan.type === 'plan' ? formatAmount({amount: plan.total}) : null}
                        {plan.type === 'plan' && plan.billingFrequency ? `/${plan.billingFrequency}` : null}
                    </Text>
                </Stack>
            </Stack>
        </Box>
    );
};

export const dateRangeCopy = (dateRange: DateRangeInclusive): string => {
    return `${moment(dateRange.fromInclusive).format('MMM D, YYYY')} - ${
        dateRange.toInclusive ? moment(dateRange.toInclusive).format('MMM D, YYYY') : ''
    }`;
};

export {PlanItem};
export type {Props};
